import {firstValueFrom} from 'rxjs';
import {IBaseCRUDComponent} from '../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {Component} from '@angular/core';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {EDeleteMode} from '../../../../sdk/src/enums/delete-mode.enum';
import {CpmCOO8ConnectorApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-oo8-connector.api-model';
import {
    NewEditOO8ConnectorModalComponent,
    NewEditOO8ConnectorModalConfig,
    NewEditOO8ConnectorModalResult
} from '../../modals/new-edit-oo8-connector-modal/new-edit-oo8-connector-modal.component';
import {CpmCOO8ConnectorApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-oo8-connector.api-service';
import {ETableColFilterType} from '../../../../sdk/src/components/generic-table/table-col-def';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';

/**
 * OO8Connector CRUD table component
 */
@Component({
    selector: 'app-oo8-connector-crud',
    templateUrl: './oo8-connector-crud.component.html',
    imports: [PrimeNgImportsModule, SdkImportsModule]
})
export class OO8ConnectorCRUDComponent extends IBaseCRUDComponent<
    CpmCOO8ConnectorApiModel,
    NewEditOO8ConnectorModalComponent,
    NewEditOO8ConnectorModalConfig,
    NewEditOO8ConnectorModalResult
> {
    protected helpVisible = false;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'OO8ConnectorApiModel',
            getModelHumanIdentifier: (model: CpmCOO8ConnectorApiModel) => model.name ?? '<Unknown OO8 Connector>',

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: (modelToDelete: CpmCOO8ConnectorApiModel) =>
                CpmCOO8ConnectorApiService.deleteOO8Connector(modelToDelete.uuid!),

            // Modal config
            modalType: NewEditOO8ConnectorModalComponent,
            getDialogServiceParameters: NewEditOO8ConnectorModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: CpmCOO8ConnectorApiModel) => new NewEditOO8ConnectorModalConfig(model),

            // Authorization config
            authorizationNew: CpmCOO8ConnectorApiService.endpointNew(),
            authorizationEdit: CpmCOO8ConnectorApiService.endpointEdit(this.FAKE_UUID),
            authorizationDelete: CpmCOO8ConnectorApiService.endpointDelete(this.FAKE_UUID)
        });

        this.modelHumanName = 'OO8 Connector';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    public override async onRefresh(): Promise<void> {
        this.loadingStr = 'Loading OO8 connectors...';

        const result = await firstValueFrom(CpmCOO8ConnectorApiService.getOO8ConnectorList({}, this.paginatorConfig));

        this.items = result.data;
        this.totalItemCount = result.total;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On help button pressed
     */
    public override async onHelp(): Promise<void> {
        this.helpVisible = true;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        this.tableCols = [
            {
                field: 'name',
                header: 'Name',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'frontend_url',
                header: 'Frontend URL',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            },
            {
                field: 'backend_url',
                header: 'Backend URL',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.TEXT
            }
        ];

        this.addCreatedColumns();
    }
}
